import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, from, Observable, of } from "rxjs";
import { MapStatus } from "../../model/map-status";
import { Feature, Point, Polygon } from "geojson";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { GeoService } from "./geo.service";
import { catchError, map, take } from "rxjs/operators";
import { ApiService, CollectionManager, OverlayManager, OrientedManager } from "@vision/core-map-api";
import { IProjectAOI } from "../../interfaces/i-project-aoi";
import { DamageStyleService } from "./damage-style.service";

@Injectable()
export class MapService {
  public mapIndex = 0;

  protected mapInitializedSubject = new BehaviorSubject<boolean>(false);

  private damageStyleService = inject(DamageStyleService);
  private damageLayerInitStarted = false;

  public mapInitialized$ = this.mapInitializedSubject.asObservable();

  protected coreMapSubject = new BehaviorSubject<any>(null);

  public coreMap$ = this.coreMapSubject.asObservable();

  protected mapStatusSubject = new BehaviorSubject<MapStatus>(null);

  public mapStatus$ = this.mapStatusSubject.asObservable();

  protected swipeStatusSubject = new BehaviorSubject<any>({
    enable: false,
    getAOIAtPoint: null,
    left: null,
    right: null,
  });

  public swipeStatus$ = this.swipeStatusSubject.asObservable();

  protected geotiffStatusSubject = new BehaviorSubject<any>(false);

  public geotiffStatus$ = this.geotiffStatusSubject.asObservable();

  protected discreteEnabledSubject = new BehaviorSubject<boolean>(false);

  public discreteEnabled$ = this.discreteEnabledSubject.asObservable();

  protected discreteOrientationSubject = new BehaviorSubject<string>(ViewerConstants.DISCRETE_ORIENTATIONS.NOT_DEFINED);

  setMapInitialized(initialized: boolean): void {
    this.mapInitializedSubject.next(initialized);
  }

  isMapInitialized(): boolean {
    return this.mapInitializedSubject.getValue();
  }

  /**
   * Get actual core map object
   * @returns {any}
   */
  getCoreMap(): any {
    return this.coreMapSubject.getValue();
  }

  setCoreMap(coreMap: any) {
    return this.coreMapSubject.next(coreMap);
  }

  refresh(): void {
    if (this.getCoreMap()) {
      this.getCoreMap().refresh();
    }
  }

  getMapIndex() {
    return this.mapIndex ? this.mapIndex : 0;
  }

  getInitConfig(): any {
    return this.getCoreMap().getInitConfig();
  }

  getApiService(): ApiService {
    return this.getCoreMap()?.getApiService();
  }

  getApiVersion(): string {
    return this.getCoreMap().getApiVersion();
  }

  getOverlayManager(): OverlayManager {
    return this.getCoreMap().getOverlayManager();
  }

  getOrientedManager(): OrientedManager {
    return this.getCoreMap().getOrientedManager();
  }

  getCollectionManager(): CollectionManager {
    return this.getCoreMap().getCollectionManager();
  }

  /**
   * Return html id for ol map
   * @returns {string}
   */
  getMapId(): string {
    return `map${this.mapIndex}`;
  }

  isMasterMap(): boolean {
    // mapIndex 0 is the Studio Viewer's single map (never assigned by Phoenix,
    // which always starts numbering at 1), so it's treated as master too.
    return this.getMapId() === "map1" || this.getMapId() === "map0";
  }

  getZoomInfo(): any {
    return this.getCoreMap().getZoomInfo();
  }

  setZoom(zoom: number) {
    this.getCoreMap().setZoom(zoom);
  }

  isDiscreteEnabled(): boolean {
    return this.discreteEnabledSubject.getValue();
  }

  setDiscreteEnabled(enabled: boolean) {
    this.discreteEnabledSubject.next(enabled);
  }

  getDiscreteOrientation(): string {
    return this.discreteOrientationSubject.getValue() === ViewerConstants.DISCRETE_ORIENTATIONS.NOT_DEFINED
      ? ViewerConstants.DISCRETE_ORIENTATIONS.N
      : this.discreteOrientationSubject.getValue();
  }

  setDiscreteOrientation(orientation: string) {
    this.discreteOrientationSubject.next(orientation);
  }

  get geotiffStatus(): boolean {
    return this.geotiffStatusSubject.getValue();
  }

  set geotiffStatus(value: boolean) {
    this.geotiffStatusSubject.next(value);
  }

  get swipeStatus(): any {
    return this.swipeStatusSubject.getValue();
  }

  set swipeStatus(value: any) {
    this.swipeStatusSubject.next(value);
  }

  /**
   * Update map status
   * Update map status observable
   * Update layers enabled by coverage
   * Update center image date
   * Update collections availabels
   * @param {MapStatus} mapStatus
   */
  setMapStatus(mapStatus: MapStatus): void {
    this.mapStatusSubject.next(mapStatus);
  }

  getMapStatus(): MapStatus {
    return this.mapStatusSubject.getValue();
  }

  getCurrentMapStatus(): any {
    return this.getCoreMap().getMapStatus(ViewerConstants.PROJECTIONS.WebMercator);
  }

  /**
   * Recalculate layers map status with old values for center, extent and zoom
   */
  populateMapStatus(): MapStatus {
    if (this.isMapInitialized()) {
      const oldStatus = this.getMapStatus();
      return this.updateMapStatus(oldStatus.center, oldStatus.extent, oldStatus.zoom);
    }
    return null;
  }

  /**
   * Calculate map status with center, extent, zoom
   * Ortholayers availables for this view extent
   * ImageryLayers available for this center
   * Update on each movement
   * @param center
   * @param extent
   * @param zoom
   */
  calculateMapStatus(center: Feature<Point>, extent: Feature<Polygon>, zoom: number): MapStatus {
    const centerMercator = GeoService.toMercator(center, false);
    const extentMercator = GeoService.toMercator(extent, false);
    return new MapStatus(
      centerMercator,
      extentMercator,
      zoom,
      this.swipeStatus,
      this.isDiscreteEnabled(),
      this.getDiscreteOrientation(),
    );
  }

  /**
   * Calculate map status and update in the ovserver
   * @param center
   * @param extent
   * @param zoom
   */
  updateMapStatus(center: Feature<Point>, extent: Feature<Polygon>, zoom: number): MapStatus {
    const status = this.calculateMapStatus(center, extent, zoom);

    this.setMapStatus(status);

    return status;
  }

  enableMeasuresSelection() {
    this.getCoreMap().enableMeasurementSelection();
  }

  allowMeasurements() {
    return this.getCoreMap().isMeasurementsAllowed();
  }

  allowProperties() {
    return this.getCoreMap().isPropertiesAllowed();
  }

  allowPropertiesReport() {
    return this.getCoreMap().isPropertiesReportAllowed();
  }

  selectMeasure(id: string) {
    this.getCoreMap().selectMeasure(id);
  }

  /**
   * Disable measurements selection
   */
  disableMeasuresSelection() {
    this.getCoreMap().disableMeasurementSelection();
  }

  enableDragMeasurement() {
    this.getCoreMap().dragMeasurementLayer(true);
  }

  disableDragMeasurement() {
    this.getCoreMap().dragMeasurementLayer(false);
  }

  enableEditMeasurement() {
    this.getCoreMap().editMeasurementLayer(true);
  }

  disableEditMeasurement() {
    this.getCoreMap().editMeasurementLayer(false);
  }

  // endregion

  changeVisibility(id: string, visibility: boolean) {
    this.getCoreMap().changeVisibility(id, visibility);
  }

  getColorRgbA(): string {
    return this.getCoreMap ? this.getCoreMap().getColorRgbA() : "rgba(255, 196, 0, 0.2)";
  }

  setColor(colorHexCode: string, features: any[]) {
    this.getCoreMap().setColor(colorHexCode, features);
  }

  setColorRgbA(colorRgba: string, features: any[]) {
    this.getCoreMap().setColorRgbA(colorRgba, features);
  }

  /**
   * Fit map to wktPolygon
   * @param {string} wktPolygon on 4326
   * @param epsg
   * @param options
   * */
  fitToWktPolygon(
    wktPolygon: string,
    epsg: string = ViewerConstants.PROJECTIONS.WebMercator,
    options: any = null,
  ): void {
    this.getCoreMap().fitToExtent(
      GeoService.wktPolygonToBbox(wktPolygon, epsg !== ViewerConstants.PROJECTIONS.WebMercator),
      ViewerConstants.PROJECTIONS.WebMercator,
      options,
    );
  }

  /**
   * Fit map to wktPolygon
   * @param {number[]} extentCoordinates array of coordinates
   * @param epsg
   * @param options
   */
  fitToExtent(
    extentCoordinates: number[],
    epsg: string = ViewerConstants.PROJECTIONS.WGS84,
    options: any = null,
  ): void {
    this.getCoreMap().fitToExtent(extentCoordinates, epsg, options);
  }

  fitToExtentWithMinZoom(
    extentCoordinates: number[],
    epsg: string = ViewerConstants.PROJECTIONS.WGS84,
    minZoom: number = ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL,
  ): void {
    this.getCoreMap().fitToExtentWithMinZoom(extentCoordinates, epsg, minZoom);
  }

  setCenterToWktPoint(wktPoint: string, epsg: string = ViewerConstants.PROJECTIONS.WebMercator): void {
    this.getCoreMap().setCenter(GeoService.wktPointToCoordinates(wktPoint), epsg);
  }

  /**
   * Set center of the map
   * @param {number[]} center as an Array
   * @param {string} epsg
   */
  setCenter(center: number[], epsg: string = ViewerConstants.PROJECTIONS.WGS84): void {
    if (center && this.getCoreMap()) {
      this.getCoreMap().setCenter(center, epsg);
    }
  }

  getMeasurementLayer(): any {
    return this.getCoreMap().getMeasurementLayer();
  }

  getDrawingLayer(): any {
    return this.getCoreMap().getDrawingLayer();
  }

  addPropertyFootprint(wkt: string, opts: any): string {
    return this.getCoreMap().addFootprint(wkt, opts);
  }

  /**
   * Fetches damage styles from the API once, stores them in {@link DamageStyleService},
   * and passes the raw style data to core-map to initialize the damage footprint layer.
   * Safe to call multiple times — only the first call triggers the request.
   */
  initDamagePropertyFootprintLayer(): void {
    if (this.damageLayerInitStarted) return;
    this.damageLayerInitStarted = true;
    from((this.getApiService() as any).getDamageBuildingStyle() as Promise<any>)
      .pipe(
        take(1),
        catchError((err) => {
          console.error("MapService: failed to fetch damage styles:", err);
          return of(null);
        }),
      )
      .subscribe((res: any) => {
        const styleData = res?.data ?? null;
        this.damageStyleService.setStyleData(styleData);
        this.getCoreMap()?.initDamagePropertyFootprintLayer(styleData);
      });
  }

  removeDamagePropertyFootprint(id: string) {
    this.getCoreMap().removeDamagePropertyFootprintFeature(id);
  }

  removePropertyFootprint(id: string) {
    this.getCoreMap().removePropertyFootprintFeature(id);
  }

  addDamagePropertyFootprint(wkt: string, opts: any): string {
    return this.getCoreMap().addDamageFootprint(wkt, opts);
  }

  getPropertyFootprintFeature(idFeature): any {
    return idFeature ? this.getCoreMap().getPropertyFootprintLayer().getSource().getFeatureById(idFeature) : null;
  }

  getDamagePropertyFootprintFeature(idFeature): any {
    return idFeature ? this.getCoreMap().getDamagePropertyFootprintLayer().getSource().getFeatureById(idFeature) : null;
  }

  /**
   * Zooms the map to the extent of a WKT geometry without requiring it to be present in any layer.
   * This works regardless of footprint visibility.
   * @param wkt - WKT string of the geometry to zoom to.
   * @param paddingPx - Viewport padding in pixels applied equally on all sides.
   */
  zoomToPropertyFootprintWkt(wkt: string, paddingPx: number): void {
    if (!wkt) return;
    this.fitToWktPolygon(wkt, ViewerConstants.PROJECTIONS.WGS84, {
      padding: [paddingPx, paddingPx, paddingPx, paddingPx],
      duration: 100,
    });
  }

  findImagesInPoint(
    center: any,
    epsg: number,
    layers: string[],
    orientation: string = null,
    projectCode: string = null,
    limit: number = null,
  ): Observable<any> {
    return from(this.getApiService().findImagesInPoint(center, layers, epsg, orientation, projectCode, limit)).pipe(
      map((response) => response),
      catchError(() => {
        return of([]);
      }),
    );
  }

  exportGeoTiff(
    layer: string,
    childAOI: string,
    zoom: number,
    cropMode: string,
    wkt: string,
    infrared: boolean = false,
  ): Observable<any> {
    return from(this.getApiService().exportGeoTiff(layer, childAOI, zoom, cropMode, wkt, infrared)).pipe(
      map((response) => response),
      catchError((err) => {
        throw new Error(err);
      }),
    );
  }

  orthoCoverage(layer: string, wkt: string): Observable<any> {
    return from(this.getApiService().orthoCoverage(layer, wkt)).pipe(
      map((response) => response),
      catchError((err) => {
        return of(err);
      }),
    );
  }

  extractAdvancedOrthoImageUrl(params: any): string {
    return this.getApiService().extractAdvancedOrthoImageUrl(params);
  }

  extractAdvancedOrthoImage(params: any): Observable<any> {
    return from(this.getApiService().extractAdvancedOrthoImage(params)).pipe(
      map((response) => response),
      catchError((err) => {
        return of(err);
      }),
    );
  }

  extractOrthoImageUrl(
    layerName: string,
    childAoi: string,
    wkt: string,
    epsg: number = ViewerConstants.GEOGRAPHIC_CODE,
    width: number = 282,
    height: number = 250,
  ): string {
    return this.getApiService().extractOrthoImageUrl(layerName, childAoi, wkt, epsg.toString(), width, height);
  }

  extractAdvancedOrientedImageUrl(params: any): string {
    return this.getApiService().extractAdvancedOrientedImageUrl(params);
  }

  extractAdvancedOrientedImage(params: any): Observable<any> {
    return from(this.getApiService().extractAdvancedOrientedImage(params)).pipe(
      map((response) => response),
      catchError((err) => {
        return of(err);
      }),
    );
  }

  /**
   * Runs AI elements detection on a WKT area via the core-map-api SDK.
   * Wraps `apiService.elementsDetect(params)` as an Observable. Errors are
   * logged and then re-thrown so callers (e.g. DetectionService) can react
   * — show a toast, revert UI stage — instead of receiving the error as a
   * regular value.
   */
  elementsDetect(params: any): Observable<any> {
    return from(this.getApiService().elementsDetect(params)).pipe(
      catchError((err) => {
        console.error("MapService.elementsDetect error:", err);
        throw err;
      }),
    );
  }

  extractImages(
    layer: string,
    zoom: number,
    center: number[],
    wkt: string = null,
    mode: string = "best",
    metadata: string = null,
    orientation: string = null,
    crop: string = null,
    nadirRotated: string = null,
    imagename: string = null,
    child: string = null,
    downsample: boolean = false,
  ): Observable<any> {
    return from(
      this.getApiService().extractImages(
        layer,
        zoom,
        center,
        wkt,
        mode,
        metadata,
        orientation,
        crop,
        nadirRotated,
        imagename,
        child,
        downsample,
      ),
    ).pipe(
      map((response) => response),
      catchError(() => {
        return of([]);
      }),
    );
  }

  extractImagesUrl(
    layer: string,
    zoom: number,
    center: number[],
    wkt: string = null,
    mode: string = "best",
    metadata: string = null,
    orientation: string = null,
    crop: string = null,
    nadirRotated: string = null,
    imagename: string = null,
    child: string = null,
  ): string {
    return this.getApiService().extractImagesUrl(
      layer,
      zoom,
      center,
      wkt,
      mode,
      metadata,
      orientation,
      crop,
      nadirRotated,
      imagename,
      child,
    );
  }

  downloadUrl(url: string): Observable<any> {
    return from(this.getApiService().downloadUrl(url)).pipe(
      map((response) => response),
      catchError((err) => {
        return of(err);
      }),
    );
  }

  pushProject(aoi: IProjectAOI) {
    this.getOverlayManager().setCollectionOnTop(aoi.getName());
  }

  pushProjectByName(aoiName: string) {
    this.getOverlayManager().setCollectionOnTop(aoiName);
  }

  getAoisByExtent(features) {
    return this.getCoreMap().getAoisByExtent(features);
  }

  findCollectionsIntersects(polygon: any, layers: string[] = null) {
    return this.getOverlayManager().findCollectionsIntersect(polygon, layers);
  }

  findCollectionsContains(point: any, layers: string[] = null) {
    return this.getOverlayManager().findCollectionsInPoint(point, layers);
  }

  findVisibleCollection(point: any) {
    return this.getOverlayManager().findVisibleCollection(point);
  }

  lockDiscreteView(enable: boolean) {
    this.getCoreMap().lockDiscrete(enable);
  }

  getDsmTileZoom() {
    return this.getCoreMap().getDSMTileZoom();
  }

  changeDsmTileZoom(zoom: number) {
    this.getCoreMap().changeDsmTileZoom(zoom);
  }

  enableDSMImageCoverage(enable: boolean) {
    this.getCoreMap().enableDSMImageCoverage(enable);
  }

  enableDSMTileStatus(enable: boolean) {
    this.getCoreMap().enableDSMTileStatus(enable);
  }

  showDSMCoverage(enable: boolean) {
    this.getCoreMap().showDSMCoverage(enable);
  }

  lockDSMDownload(enable: boolean) {
    this.getCoreMap().lockDSMDownload(enable);
  }

  getDSMCachedInfo() {
    return this.getCoreMap().getDSMCachedInfo();
  }

  cleanDSMTilesCache() {
    this.getCoreMap().cleanDSMTilesCache();
  }

  addLayer(layer: any) {
    this.getCoreMap()?.olMap.addLayer(layer);
  }

  removeLayer(layer: any): void {
    this.getCoreMap()?.olMap.removeLayer(layer);
  }

  findLayer(id: string): any {
    return this.getCoreMap()?.getLayerById(id) ?? null;
  }
}
